import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { Row, Col, Switch, Button, Dropdown, Menu, Modal, Space, Tag, Typography, message } from 'antd';
import moment from 'moment';
import { EditOutlined, UpSquareOutlined, WalletOutlined, MoreOutlined, SettingOutlined, LogoutOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { getApi } from 'redux/sagas/getApiDataSaga';
import { putApi } from 'redux/sagas/putApiSaga';
import { resetApiStatus } from 'redux/reducers/globals/globalActions';
import { API_STATUS, DATE_FORMAT } from 'constants/app-constants';
import { SERVER_IP } from 'assets/Config';
import { ADMIN_MENU_ACCESS, DEFAULT_ENABLED_ADMIN_MENU_KEYS, getInitialGymMenuKeys } from 'helpers/menu-access';
import HighlightComponent from 'components/HighlightComponent';
import { generatePagination } from 'helpers';
import { getDateFormat, getDifferenceDays, getINRMoneyFormat } from 'services/Utils';
import GymListPresentational from './gym-list-presentational';
import { useI18n } from 'hooks/useI18n';
import { postApi } from 'redux/sagas/postApiDataSaga';
import { triggerMenuSettingsUpdate, triggerForceLogout } from '../../../../firebase';

const initialPageSize = 10;
const initialPageSizeOptions = [10, 15, 20];

const GymListFunctional = React.memo(() => {
	const { t } = useI18n();
	const dispatch = useDispatch();
	const gymRedux = useSelector((state) => state.gymRedux);
	const globalRedux = useSelector((state) => state.globalRedux);

	const [selectedRowKeys, setSelectedRowKeys] = useState([]);
	const [gymAddModal, setGymAddModal] = useState(false);
	const [payNowAddModal, setPayNowAddModal] = useState(false);
	const [renewGymModal, setRenewGymModal] = useState(false);
	const [menuSettingsModalOpen, setMenuSettingsModalOpen] = useState(false);
	const [menuSettingsGym, setMenuSettingsGym] = useState(null);
	const [enabledMenusDraft, setEnabledMenusDraft] = useState(DEFAULT_ENABLED_ADMIN_MENU_KEYS);
	const [updatingMenuAccess, setUpdatingMenuAccess] = useState(false);
	const [forceLogoutModal, setForceLogoutModal] = useState(false);
	const [forceLogoutTarget, setForceLogoutTarget] = useState(null);
	const [forceLogoutLoading, setForceLogoutLoading] = useState(false);

	const [editGym, setEditGym] = useState(null);
	const [renewGym, setRenewGym] = useState(null);
	const [searchKey, setSearchKey] = useState('');
	const [tableData, setTableData] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(initialPageSize);

	const didInitialFetch = useRef(false);

	const fetchGyms = useCallback(() => {
		dispatch(getApi('GET_GYMS', `${SERVER_IP}api/users`));
	}, [dispatch]);

	useEffect(() => {
		if (!didInitialFetch.current) {
			fetchGyms();
			didInitialFetch.current = true;
		}
	}, [fetchGyms]);

	useEffect(() => {
		setTableData(gymRedux.gyms || []);
		generatePagination(gymRedux.gyms || []);
	}, [gymRedux.gyms]);

	useEffect(() => {
		const { DELETE_GYM, CHANGE_STATUS } = globalRedux.apiStatus;
		if (DELETE_GYM === 'SUCCESS' || CHANGE_STATUS === 'SUCCESS') {
			fetchGyms();
			if (DELETE_GYM === 'SUCCESS') dispatch(resetApiStatus('DELETE_GYM'));
			if (CHANGE_STATUS === 'SUCCESS') dispatch(resetApiStatus('CHANGE_STATUS'));
		}
	}, [globalRedux.apiStatus, fetchGyms, dispatch]);

	const configurableMenuOptions = useMemo(
		() =>
			ADMIN_MENU_ACCESS.filter((item) => !item.alwaysEnabled).map((item) => ({
				label: t(item.labelKey),
				value: item.key,
			})),
		[t]
	);

	const filteredData = useMemo(() => {
		if (!searchKey.trim()) return tableData;
		const key = searchKey.toLowerCase();
		return tableData.filter((gym) =>
			[gym?.gymName, gym?.mobile, gym?.email, gym?.address].some((field) => (field || '').toLowerCase().includes(key))
		);
	}, [tableData, searchKey]);

	const handleEdit = (row) => {
		setEditGym(row);
		setGymAddModal(true);
	};

	const handleAddGym = () => {
		setEditGym(null);
		setGymAddModal(true);
	};

	const handlePayNow = (row) => {
		setEditGym(row);
		setPayNowAddModal(true);
	};

	const handleRenew = (row) => {
		setRenewGym(row);
		setRenewGymModal(true);
	};

	const handleForceLogout = (row) => {
		setForceLogoutTarget(row);
		setForceLogoutModal(true);
	};

	const handleCancelForceLogout = () => {
		if (forceLogoutLoading) return;
		setForceLogoutModal(false);
		setForceLogoutTarget(null);
	};

	const handleConfirmForceLogout = async () => {
		setForceLogoutLoading(true);
		try {
			await triggerForceLogout(forceLogoutTarget.id);
			message.success(t('gyms.actions.force_logout_success', { gymName: forceLogoutTarget.gymName }));
			setForceLogoutModal(false);
			setForceLogoutTarget(null);
		} catch (err) {
			console.error('Force logout failed:', err);
			message.error(t('gyms.actions.force_logout_error'));
		} finally {
			setForceLogoutLoading(false);
		}
	};

	const handleOpenMenuSettings = (row) => {
		setMenuSettingsGym(row);
		setEnabledMenusDraft(getInitialGymMenuKeys(row));
		setMenuSettingsModalOpen(true);
	};

	const handleSaveMenuSettings = () => {
		if (!menuSettingsGym?.id) {
			return;
		}

		setUpdatingMenuAccess(true);
		const data = {
			gymId: menuSettingsGym.id,
			gymName: menuSettingsGym.gymName,
			email: menuSettingsGym.email,
			enabledMenus: enabledMenusDraft,
		};
		dispatch(postApi(data, 'ADD_MENU_SETTINGS'));
	};

	const loading = globalRedux.apiStatus.ADD_MENU_SETTINGS === API_STATUS.PENDING;

	useEffect(() => {
		if (globalRedux.apiStatus.ADD_MENU_SETTINGS === 'SUCCESS') {
			dispatch(resetApiStatus('ADD_MENU_SETTINGS'));
			setTableData((currentTableData) =>
				currentTableData.map((gym) =>
					gym.id === menuSettingsGym.id
						? {
							...gym,
							enabledMenus: enabledMenusDraft,
						}
						: gym
				)
			);

			// Signal gym admin browsers to refresh menu settings in real time.
			triggerMenuSettingsUpdate(menuSettingsGym.id);

			message.success(t('gyms.menu_settings.saved_message'));
			setMenuSettingsModalOpen(false);
			setMenuSettingsGym(null);
			setUpdatingMenuAccess(false);
		}
	}, [globalRedux.apiStatus.ADD_MENU_SETTINGS, dispatch, t, menuSettingsGym, enabledMenusDraft]);

	const handleActivation = (isAccountActivated, row) => {
		dispatch(putApi({ isAccountActivated }, 'CHANGE_STATUS', `${SERVER_IP}api/users/changestatus/${row.id}`));
	};

	const handleClose = useCallback(() => {
		setGymAddModal(false);
		setPayNowAddModal(false);
		setRenewGymModal(false);
		setMenuSettingsModalOpen(false);
		setEditGym(null);
		setRenewGym(null);
		setMenuSettingsGym(null);
		setEnabledMenusDraft(DEFAULT_ENABLED_ADMIN_MENU_KEYS);
	}, []);

	const tableLoading = globalRedux.apiStatus.GET_GYMS === API_STATUS.PENDING;
	const changingStatus = globalRedux.apiStatus.CHANGE_STATUS === API_STATUS.PENDING;

	const rowSelection = {
		selectedRowKeys,
		onChange: setSelectedRowKeys,
	};

	const handleTableChange = (page, size) => {
		setCurrentPage(page);
		setPageSize(size);
	};

	const getStartingValue = () => (currentPage - 1) * pageSize + 1;
	const getEndingValue = () => Math.min(currentPage * pageSize, tableData.length);

	// Menu for actions (Edit, Pay Now, Renew)
	const renderMenu = (row) => {
		const dueInFuture = getDifferenceDays(new Date(row.nextBillDate), new Date()) > 0;
		const hasBalance = Number(row?.balance) > 0;
		const showPay = hasBalance;
		const showRenew = !dueInFuture && !hasBalance;

		return (
			<Menu>
				<Menu.Item key="edit" onClick={() => handleEdit(row)} icon={<EditOutlined />}>
					{t('common.edit')}
				</Menu.Item>
				<Menu.Item key="menu-settings" onClick={() => handleOpenMenuSettings(row)} icon={<SettingOutlined />}>
					{t('gyms.actions.menu_settings')}
				</Menu.Item>
				{showPay && (
					<Menu.Item key="pay-now" onClick={() => handlePayNow(row)} icon={<WalletOutlined style={{ color: '#52c41a' }} />}>
						<span style={{ color: '#52c41a', fontWeight: 500 }}>{t('gyms.actions.pay_balance')}</span>
					</Menu.Item>
				)}
				{showRenew && (
					<Menu.Item key="renew" onClick={() => handleRenew(row)} icon={<UpSquareOutlined style={{ color: '#fa8c16' }} />}>
						<span style={{ color: '#fa8c16', fontWeight: 500 }}>{t('gyms.actions.renew')}</span>
					</Menu.Item>
				)}
				<Menu.Divider />
				<Menu.Item key="force-logout" onClick={() => handleForceLogout(row)} icon={<LogoutOutlined />} danger>
					{t('gyms.actions.force_logout')}
				</Menu.Item>
			</Menu>
		);
	};

	const columns = [
		{
			title: '#',
			key: 'index',
			render: (_, __, index) => index + 1,
			fixed: 'left',
		},
		{
			title: t('gyms.columns.gym_name'),
			dataIndex: 'gymName',
			sorter: (a, b) => a?.gymName?.localeCompare(b?.gymName),
			render: (value) => (
				<strong>
					<HighlightComponent textToHighlight={value} searchWords={[searchKey]} autoEscape />
				</strong>
			),
		},
		{
			title: t('gyms.columns.owner_name'),
			key: 'firstName',
			sorter: (a, b) => a?.firstName?.localeCompare(b?.firstName),
			render: (_, row) => (
				<strong>
					<HighlightComponent textToHighlight={`${row.firstName} ${row.lastName || ''}`} searchWords={[searchKey]} autoEscape />
				</strong>
			),
		},
		{
			title: t('gyms.columns.mobile'),
			dataIndex: 'mobile',
			sorter: (a, b) => a.mobile - b.mobile,
			render: (value) => <HighlightComponent textToHighlight={value} searchWords={[searchKey]} autoEscape />,
		},
		{
			title: t('gyms.columns.email'),
			dataIndex: 'email',
			sorter: (a, b) => a?.email?.localeCompare(b?.email),
			render: (value) => <HighlightComponent textToHighlight={value} searchWords={[searchKey]} autoEscape />,
		},
		{
			title: t('gyms.columns.status'),
			dataIndex: 'isAccountActivated',
			render: (value, row) => (
				<Switch
					checked={value}
					onChange={(val) => handleActivation(val, row)}
					checkedChildren={t('common.active')}
					unCheckedChildren={t('common.inactive')}
					loading={changingStatus}
				/>
			),
		},
		{
			title: t('gyms.columns.next_billing_date'),
			dataIndex: 'nextBillDate',
			align: 'left',
			render: (value) => (
				<HighlightComponent searchWords={[searchKey]} autoEscape={true} textToHighlight={value ? moment(value).format(DATE_FORMAT.DD_MM_YYYY) : '-'} />
			),
		},
		{
			title: t('gyms.columns.amount_paid'),
			dataIndex: 'amount',
			align: 'right',
			width: '8%',
			render: (value) => getINRMoneyFormat(value),
		},
		{
			title: t('gyms.columns.balance'),
			dataIndex: 'balance',
			align: 'right',
			width: '8%',
			render: (value) =>
				value ? (
					<div
						style={{
							color: 'red',
							fontWeight: 'bold',
						}}>
						{getINRMoneyFormat(value)}
					</div>
				) : (
					' - '
				),
		},
		{
			title: t('gyms.columns.joined_on'),
			dataIndex: 'created_at',
			align: 'left',
			width: '10%',
			render: (value) => getDateFormat(value),
		},
		{
			title: t('gyms.columns.action'),
			key: 'actions',
			render: (_, row) => (
				<Dropdown overlay={renderMenu(row)} trigger={['click']}>
					<Button icon={<MoreOutlined />} />
				</Dropdown>
			),
		},
	];

	return (
		<>
		<GymListPresentational
			{...{
				filteredData,
				changingStatus,
				column: columns,
				tableLoading,
				rowSelection,
				selectedRowKeys,
				handleAddGym,
				currentPage,
				pageSize,
				initialPageSizeOptions,
				initialPageSize,
				handleTableChange,
				setSearchKey,
				getStartingValue,
				getEndingValue,
				gymAddModal,
				setGymAddModal,
				refreshList: fetchGyms,
				editGym,
				handleClose,
				menuSettingsModalOpen,
				menuSettingsGym,
				enabledMenusDraft,
				setEnabledMenusDraft,
				handleSaveMenuSettings,
				configurableMenuOptions,
				defaultEnabledMenus: DEFAULT_ENABLED_ADMIN_MENU_KEYS,
				updatingMenuAccess,
				payNowAddModal,
				setPayNowAddModal,
				renewGymModal,
				renewGym,
				setRenewGym,
				setRenewGymModal,
			}}
		/>

		<Modal
			open={forceLogoutModal}
			onCancel={handleCancelForceLogout}
			closeIcon={!forceLogoutLoading}
			maskedClosable={false}
			footer={[
				<Button key="cancel" onClick={handleCancelForceLogout} disabled={forceLogoutLoading}>
					{t('common.cancel')}
				</Button>,
				<Button key="confirm" type="primary" danger loading={forceLogoutLoading} onClick={handleConfirmForceLogout}>
					<LogoutOutlined /> {t('gyms.actions.force_logout')}
				</Button>,
			]}
			title={
				<Space>
					<ExclamationCircleOutlined style={{ color: 'var(--error-color)', fontSize: 18 }} />
					<span>{t('gyms.actions.force_logout')}</span>
				</Space>
			}
			width={420}
		>
			<div style={{ padding: '8px 0 4px' }}>
				<div className="force-logout-gym-card">
					<LogoutOutlined style={{ color: 'var(--error-color)', fontSize: 20 }} />
					<div>
						<Typography.Text strong style={{ display: 'block', fontSize: 14 }}>
							{forceLogoutTarget?.gymName}
						</Typography.Text>
						<Typography.Text type="secondary" style={{ fontSize: 12 }}>
							{forceLogoutTarget?.email}
						</Typography.Text>
					</div>
				</div>
				<Typography.Text>{t('gyms.actions.force_logout_confirm_content')}</Typography.Text>
			</div>
		</Modal>
		</>
	);
});

export default GymListFunctional;
